import React, { useEffect, useRef, useState } from 'react';
import { Spin, Tag, Typography } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { normalizeToPercent, getScoreLabel, ScoreBadge, formatScoreDisplay } from './summarizationUtils';

const { Text } = Typography;

const DEFAULT_STORAGE_KEY = 'teacher_eval_start';

const formatElapsed = (totalSeconds) => {
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}m ${s}s`;
};

const ScoreItem = ({ modelName, score }) => {
  const displayScore = formatScoreDisplay(score);
  if (displayScore === null) return null;
  const pct = normalizeToPercent(score);
  const badge = getScoreLabel(pct);
  if (badge === ScoreBadge.NoData) return null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <Text style={{ fontSize: 14 }}>{modelName} = {displayScore}</Text>
      <Tag style={{ margin: 0 }}>{badge}</Tag>
    </span>
  );
};

const OverallScoresRow = ({
  groundTruthCard,
  studentCard,
  teacherCard,
  teacherLoading = false,
  teacherLoadingMessage,
  showTeacher = true,
  teacherModelName,
  teacherStartedAt,
  storageKey = DEFAULT_STORAGE_KEY,
}) => {
  const [elapsed, setElapsed] = useState(0);
  const loadingKeyRef = useRef(null);

  useEffect(() => {
    if (!teacherLoading) {
      loadingKeyRef.current = null;
      setElapsed(0);
      return;
    }

    loadingKeyRef.current = storageKey;

    let origin;
    if (teacherStartedAt) {
      origin = new Date(teacherStartedAt).getTime();
    } else {
      try {
        const stored = localStorage.getItem(storageKey);
        origin = stored ? parseInt(stored, 10) : null;
      } catch { origin = null; }
      if (!origin || origin > Date.now()) {
        origin = Date.now();
      }
    }

    try { localStorage.setItem(storageKey, String(origin)); } catch {}

    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - origin) / 1000)));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [teacherLoading, teacherStartedAt, storageKey]);

  const hasTeacherScore = teacherCard?.score != null && teacherCard?.score !== '';
  useEffect(() => {
    if (hasTeacherScore && loadingKeyRef.current) {
      try { localStorage.removeItem(loadingKeyRef.current); } catch {}
      loadingKeyRef.current = null;
    }
  }, [hasTeacherScore]);

  const showTeacherLoading = showTeacher && teacherLoading && teacherLoadingMessage;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap', padding: '4px 0' }}>
      <Text style={{ color: '#8c8c8c', fontSize: 14, fontWeight: 400 }}>Overall Scores:</Text>

      <ScoreItem
        modelName={groundTruthCard?.model_name || 'Benchmark'}
        score={groundTruthCard?.score}
      />

      <ScoreItem
        modelName={studentCard?.model_name || 'Specific AI'}
        score={studentCard?.score}
      />

      {showTeacher && (
        showTeacherLoading ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#8c8c8c', fontSize: 14 }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 14 }} spin />} size="small" />
            <span>{teacherLoadingMessage}</span>
            {elapsed > 0 && <span>({formatElapsed(elapsed)})</span>}
          </span>
        ) : (
          <ScoreItem
            modelName={teacherModelName || 'Teacher'}
            score={teacherCard?.score}
          />
        )
      )}
    </div>
  );
};

export default React.memo(OverallScoresRow);
